import { useId, type CSSProperties, type ReactNode } from "react";
import type {
  CrestParams,
  KitParams,
  StadiumParams,
  TitleParams,
} from "@legacy-manager/shared";
import { useT } from "../i18n";

/**
 * Cosméticos desenhados por código.
 *
 * Nenhum item comprado é um arquivo de imagem. O que chega do servidor é um
 * punhado de parâmetros (forma, padrão, cores, texto) e o desenho acontece
 * aqui, em SVG e em CSS, do mesmo jeito que o `Crest` dos átomos já desenhava
 * escudo a partir de duas cores do banco.
 *
 * Isso decide três coisas de uma vez. O repositório fica sem binário, o
 * aplicativo não cresce a cada item novo, e um cosmético inédito é uma linha no
 * catálogo compartilhado, não um upload.
 *
 * O que **não** acontece aqui: decidir se a pessoa tem o item. Quem responde
 * isso é o servidor, e o que chega já vem filtrado pelo direito. Estes
 * componentes recebem parâmetros ou `null`, e `null` quer dizer desenhar o de
 * sempre.
 */

// ---------------------------------------------------------------------------
// Escudo
// ---------------------------------------------------------------------------

/** Contorno de cada forma, no mesmo `viewBox` do escudo padrão dos átomos. */
const CONTORNO: Record<CrestParams["shape"], string> = {
  shield: "M20 1 38 6v20c0 8-8 14-18 17C10 40 2 34 2 26V6Z",
  circle: "M20 3a19 19 0 1 1 0 38 19 19 0 1 1 0-38Z",
  diamond: "M20 1 38 22 20 43 2 22Z",
  pennant: "M7 2h26v38l-13-9-13 9Z",
};

/**
 * Escudo de um cosmético.
 *
 * Mesmo `viewBox` e mesma tipografia do `Crest` dos átomos, de propósito: o
 * escudo comprado troca de forma e de cor, nunca de tamanho nem de peso na
 * linha. Uma lista com um clube comprado e cinco clubes comuns precisa
 * continuar alinhada.
 */
export function EscudoCosmetico({
  params,
  short,
  size = 40,
  semSigla = false,
}: {
  params: CrestParams;
  short: string;
  size?: number;
  /** Tira a sigla de dentro do escudo, para quem a escreve por fora. */
  semSigla?: boolean;
}): ReactNode {
  // Dois escudos na mesma tela não podem dividir o mesmo recorte, senão o
  // segundo herda a forma do primeiro.
  const recorte = `escudo-${useId()}`;

  return (
    <svg width={size} height={size} viewBox="0 0 40 44" aria-hidden="true" style={{ flexShrink: 0 }}>
      <defs>
        <clipPath id={recorte}>
          <path d={CONTORNO[params.shape]} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${recorte})`}>
        <rect x="0" y="0" width="40" height="44" fill={params.primary} />
        <DesenhoDoEscudo params={params} />
      </g>
      <path
        d={CONTORNO[params.shape]}
        fill="none"
        stroke={params.detail}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {!semSigla && (
        <text
          x="20"
          y={params.shape === "pennant" ? 24 : 26}
          textAnchor="middle"
          fontSize="13"
          fontWeight="800"
          fill="#fff"
          style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.45)", strokeWidth: 3 }}
        >
          {short}
        </text>
      )}
    </svg>
  );
}

/** O que vai por dentro do contorno, já recortado por quem chamou. */
function DesenhoDoEscudo({ params }: { params: CrestParams }): ReactNode {
  const { pattern, secondary, detail } = params;

  if (pattern === "halves") {
    return <rect x="20" y="0" width="20" height="44" fill={secondary} />;
  }

  if (pattern === "sash") {
    return <path d="M-6 34 L28 -6 L40 4 L6 44 Z" fill={secondary} />;
  }

  if (pattern === "stripes") {
    return (
      <>
        {[2, 12, 22, 32].map((x) => (
          <rect key={x} x={x} y="0" width="6" height="44" fill={secondary} />
        ))}
      </>
    );
  }

  if (pattern === "star") {
    return (
      <>
        <rect x="0" y="0" width="40" height="14" fill={secondary} />
        <path
          d="M20 28l2.6 5.4 5.9.8-4.3 4.2 1.1 5.9L20 41.5l-5.3 2.8 1.1-5.9-4.3-4.2 5.9-.8Z"
          fill={detail}
        />
      </>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Uniforme
// ---------------------------------------------------------------------------

/** Silhueta da camisa: ombros, mangas, corpo e a gola em recorte. */
const CAMISA = "M14 3 L7 5 L2 14 L8 19 L12 16 V37 H28 V16 L32 19 L38 14 L33 5 L26 3 A6.5 6.5 0 0 1 14 3 Z";

/**
 * Uniforme do clube.
 *
 * O jogo nunca desenhou camisa antes, então este é o único lugar em que ela
 * existe: um símbolo do clube ao lado do escudo, no tamanho de um ícone. Não é
 * modelo tridimensional nem textura, é a mesma geometria simples do resto.
 */
export function UniformeCosmetico({
  params,
  size = 34,
}: {
  params: KitParams;
  size?: number;
}): ReactNode {
  const recorte = `camisa-${useId()}`;

  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true" style={{ flexShrink: 0 }}>
      <defs>
        <clipPath id={recorte}>
          <path d={CAMISA} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${recorte})`}>
        <rect x="0" y="0" width="40" height="40" fill={params.primary} />
        <DesenhoDaCamisa params={params} />
      </g>
      <path d={CAMISA} fill="none" stroke="rgba(0,0,0,0.45)" strokeWidth="1.4" strokeLinejoin="round" />
      {/* Gola e punhos ficam fora do recorte: são o acabamento, não o padrão. */}
      <path d="M14 3 A6.5 6.5 0 0 0 26 3" fill="none" stroke={params.detail} strokeWidth="2.6" />
      <path d="M4 16 L8 19" stroke={params.detail} strokeWidth="2.6" strokeLinecap="round" />
      <path d="M36 16 L32 19" stroke={params.detail} strokeWidth="2.6" strokeLinecap="round" />
    </svg>
  );
}

function DesenhoDaCamisa({ params }: { params: KitParams }): ReactNode {
  const { pattern, secondary } = params;

  if (pattern === "halves") {
    return <rect x="20" y="0" width="20" height="40" fill={secondary} />;
  }

  if (pattern === "sash") {
    return <path d="M-4 30 L26 -4 L36 4 L6 38 Z" fill={secondary} />;
  }

  if (pattern === "stripes") {
    return (
      <>
        {[3, 11, 19, 27, 35].map((x) => (
          <rect key={x} x={x} y="0" width="4" height="40" fill={secondary} />
        ))}
      </>
    );
  }

  if (pattern === "hoops") {
    return (
      <>
        {[6, 16, 26, 36].map((y) => (
          <rect key={y} x="0" y={y} width="40" height="5" fill={secondary} />
        ))}
      </>
    );
  }

  if (pattern === "checks") {
    const casas: ReactNode[] = [];
    for (let linha = 0; linha < 8; linha += 1) {
      for (let coluna = 0; coluna < 8; coluna += 1) {
        if ((linha + coluna) % 2 === 0) continue;
        casas.push(
          <rect
            key={`${linha}-${coluna}`}
            x={coluna * 5}
            y={linha * 5}
            width="5"
            height="5"
            fill={secondary}
          />,
        );
      }
    }
    return <>{casas}</>;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Tema de estádio
// ---------------------------------------------------------------------------

/**
 * Tema do estádio aplicado ao cabeçalho do clube.
 *
 * Tinta por cima do fundo de sempre, em vez de trocá-lo. O tema da tarde tem
 * céu claro e o da noite tem céu quase preto: pintar o cabeçalho inteiro com
 * eles deixaria o texto ilegível em um dos dois. Uma camada translúcida dá a
 * cor do estádio e mantém o contraste que a tela já tinha.
 */
export function estiloDeEstadio(params: StadiumParams | null): CSSProperties {
  if (!params) return {};
  return {
    background: `linear-gradient(180deg, ${params.sky}2e 0%, ${params.stands}3d 46%, ${params.grass}59 100%), linear-gradient(180deg, var(--surface-2), var(--surface))`,
    borderBottom: `2px solid ${params.grass}`,
    boxShadow: `inset 0 1px 0 ${params.lights}40`,
  };
}

/**
 * O estádio como figura, para a vitrine e para o vestiário.
 *
 * O cabeçalho do clube usa o gradiente acima porque a largura dele varia com a
 * tela. Aqui a peça tem tamanho conhecido, então dá para desenhar o que o tema
 * realmente promete: refletor, arquibancada, gramado e as faixas do corte.
 */
export function MiniEstadio({
  params,
  largura = 104,
  altura = 62,
}: {
  params: StadiumParams;
  largura?: number;
  altura?: number;
}): ReactNode {
  return (
    <svg
      width={largura}
      height={altura}
      viewBox="0 0 104 62"
      aria-hidden="true"
      style={{ flexShrink: 0, borderRadius: 6, display: "block" }}
    >
      <rect x="0" y="0" width="104" height="62" fill={params.sky} />
      <circle cx="18" cy="8" r="9" fill={params.lights} opacity="0.5" />
      <circle cx="86" cy="8" r="9" fill={params.lights} opacity="0.5" />
      <rect x="15" y="7" width="6" height="2.4" rx="1.2" fill={params.lights} />
      <rect x="83" y="7" width="6" height="2.4" rx="1.2" fill={params.lights} />

      <path d="M0 22 H104 V38 H0 Z" fill={params.stands} />
      {[26, 31].map((y) =>
        [8, 20, 32, 44, 56, 68, 80, 92].map((x) => (
          <rect key={`${x}-${y}`} x={x} y={y} width="7" height="3" rx="1" fill={params.lights} opacity="0.35" />
        )),
      )}

      <path d="M0 38 H104 V62 H0 Z" fill={params.grass} />
      {[0, 20, 40, 60, 80].map((x) => (
        <rect key={x} x={x} y="38" width="10" height="24" fill="#ffffff" opacity="0.06" />
      ))}
      <ellipse cx="52" cy="50" rx="13" ry="7" fill="none" stroke="#ffffff" strokeWidth="1.2" opacity="0.55" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Título de treinador
// ---------------------------------------------------------------------------

/**
 * Título ao lado do nome.
 *
 * Fica com a cara de etiqueta e não de medalha: o título é enfeite de quem
 * comprou, e quem não comprou não pode ficar com a impressão de estar vendo um
 * troféu que não ganhou.
 */
export function TituloDeTreinador({
  params,
  size = 10,
}: {
  params: TitleParams;
  size?: number;
}): ReactNode {
  const t = useT();
  return (
    <span
      title={t("cosmeticos.tituloAjuda")}
      style={{
        display: "inline-block",
        fontSize: size,
        fontWeight: 800,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: params.color,
        border: `1px solid ${params.color}`,
        borderRadius: 4,
        padding: "1px 5px",
        lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      {params.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Prévia da loja e do vestiário
// ---------------------------------------------------------------------------

/** Os parâmetros de qualquer um dos quatro tipos, como o servidor os manda. */
export type ParametrosDeCosmetico = CrestParams | KitParams | StadiumParams | TitleParams;

/*
 * O servidor manda `kind` ao lado dos parâmetros, e não dentro deles, então o
 * estreitamento é feito pela forma. Conferir a chave é mais honesto que um
 * `as`: um parâmetro que mude de forma no servidor passa a cair no ramo errado
 * na hora da compilação, e não na hora de desenhar.
 */
function ehEscudo(params: ParametrosDeCosmetico): params is CrestParams {
  return "shape" in params;
}

function ehEstadio(params: ParametrosDeCosmetico): params is StadiumParams {
  return "grass" in params;
}

function ehTitulo(params: ParametrosDeCosmetico): params is TitleParams {
  return "label" in params;
}

/**
 * Prévia de um item, do tamanho de um selo.
 *
 * É o que a vitrine mostra no lugar da caixa cinza: o escudo que a pessoa vai
 * levar, desenhado com os parâmetros que o servidor declarou para aquele `sku`.
 */
export function PreviaDeCosmetico({
  params,
  tamanho = 48,
}: {
  params: ParametrosDeCosmetico;
  tamanho?: number;
}): ReactNode {
  const t = useT();

  if (ehEscudo(params)) {
    return <EscudoCosmetico params={params} short={t("cosmeticos.siglaDaPrevia")} size={tamanho} />;
  }
  if (ehEstadio(params)) {
    return <MiniEstadio params={params} largura={tamanho * 2.1} altura={tamanho * 1.25} />;
  }
  if (ehTitulo(params)) {
    return <TituloDeTreinador params={params} size={11} />;
  }
  return <UniformeCosmetico params={params} size={tamanho} />;
}
